import { Controller } from "@hotwired/stimulus"
import { track as mixpanelTrack } from "../lib/mixpanel"

export default class extends Controller {
  static targets = [
    "overlay", "image", "video", "imageWrapper", "annotationCanvas", "annotationTooltip",
    "commentList", "commentInput", "commentForm",
    "counter", "panel", "sourceBadge", "sourceLabel", "sourceDetailWrap",
    "dateLabel", "userLabel", "takenLabel", "rotateBtn", "markupBtn",
    "locationRow", "locationCoords", "locationChevron", "mapWrap", "mapFrame",
    "facingRow", "facingArrow", "facingLabel",
    "deleteConfirmModal", "deletePhotoBtn", "editCommentModal", "editCommentInput",
    "deleteCommentModal", "downloadBtn", "downloadIcon", "downloadSpinner",
    "coverBtn", "coverIcon", "coverSpinner", "coverConfirmModal", "coverLockCheckbox",
    "aiRow", "aiToggleRow", "aiChevron", "aiDesc", "aiTags", "radarBtn", "radarIcon", "radarSpinner", "radarSection", "radarContent",
    "radarBoxes", "videoNote"
  ]

  static values = {
    projectId: Number,
    currentUserId: Number,
    currentUserName: String,
    baseUrl: String,
    canSetCover: Boolean
  }

  connect() {
    this._photos = []
    this._index = -1
    this._onKeydown = this._handleKeydown.bind(this)
  }

  // ── Public API ────────────────────────────────────────────────────

  open({ photos, index }) {
    this._photos = photos
    this._index = index
    this.overlayTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    document.addEventListener("keydown", this._onKeydown)
    this._showPhoto(index)
  }

  close() {
    if (this._annotationSC) {
      this._annotationSC.dispose()
      this._annotationSC = null
    }
    if (this.hasAnnotationCanvasTarget) {
      this.annotationCanvasTarget.style.display = "none"
    }
    if (this.hasAnnotationTooltipTarget) {
      this.annotationTooltipTarget.classList.add("hidden")
    }
    this._stopVideoWatch()
    if (this.hasVideoTarget) {
      try { this.videoTarget.pause() } catch (_) {}
      this.videoTarget.removeAttribute("src")
      this.videoTarget.load()
    }
    this._collapseLocationMap(true)
    this.overlayTarget.classList.add("hidden")
    document.body.style.overflow = ""
    document.removeEventListener("keydown", this._onKeydown)
    this._photos = []
    this._index = -1
  }

  next() {
    if (this._index < this._photos.length - 1) {
      this._index++
      this._showPhoto(this._index)
    }
  }

  prev() {
    if (this._index > 0) {
      this._index--
      this._showPhoto(this._index)
    }
  }

  // ── Videos this browser can't play (Dorothy Shupe 2026-10-05) ─────
  // An H.265 phone or drone video plays on a Mac but often not in Chrome on
  // Windows: sound with no picture, or an error. The server then makes a
  // copy that plays everywhere (prepare_video → PreparePhotoVideoJob); the
  // note says so, and the copy is swapped in once it's ready.

  _watchVideo(photo) {
    this._stopVideoWatch()
    const video = this.videoTarget
    const check = () => {
      if (this._photos[this._index] !== photo) return
      if (video.error || (video.readyState >= 1 && !video.videoWidth)) this._cannotPlay(photo)
    }
    this._videoCheck = check
    video.addEventListener("loadedmetadata", check)
    video.addEventListener("error", check)
  }

  _stopVideoWatch() {
    if (this._videoCheck && this.hasVideoTarget) {
      this.videoTarget.removeEventListener("loadedmetadata", this._videoCheck)
      this.videoTarget.removeEventListener("error", this._videoCheck)
    }
    this._videoCheck = null
    clearTimeout(this._videoPoll)
    this._videoPoll = null
    if (this.hasVideoNoteTarget) this.videoNoteTarget.classList.add("hidden")
  }

  async _cannotPlay(photo) {
    // Ask once per video; the server knows whether a copy is coming, made or failed.
    if (!photo._copyAsked && ["queued", "making", "ready"].indexOf(photo.playback_status) === -1) {
      photo._copyAsked = true
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      try {
        const res = await fetch(`${this.baseUrlValue}/${photo.id}/prepare_video`, {
          method: "POST",
          headers: { "X-CSRF-Token": token || "", "Accept": "application/json" },
          credentials: "same-origin"
        })
        if (res.ok) {
          const data = await res.json()
          photo.playback_status = data.playback_status
        }
      } catch (_) {}
    }
    if (this._photos[this._index] !== photo) return

    const preparing = photo.playback_status === "queued" || photo.playback_status === "making"
    this._showVideoNote(preparing ? "preparing" : "cannotPlay")
    if (preparing && !this._videoPoll) this._pollVideoCopy(photo)
  }

  _showVideoNote(kind) {
    if (!this.hasVideoNoteTarget) return
    const note = this.videoNoteTarget
    note.textContent = note.dataset[kind] || ""
    note.classList.remove("hidden")
  }

  // Every 15 s while this video is on screen, until the copy is ready.
  _pollVideoCopy(photo) {
    this._videoPoll = setTimeout(async () => {
      this._videoPoll = null
      if (this._photos[this._index] !== photo) return
      try {
        const res = await fetch(`${this.baseUrlValue}/${photo.id}`, { headers: { "Accept": "application/json" }, credentials: "same-origin" })
        if (res.ok) {
          const data = await res.json()
          photo.playback_status = data.playback_status
          if (data.optimized_url) photo.optimized_url = data.optimized_url
        }
      } catch (_) {}
      if (this._photos[this._index] !== photo) return

      if (photo.playback_status === "ready") {
        this.videoNoteTarget.classList.add("hidden")
        this._watchVideo(photo)
        this.videoTarget.src = photo.optimized_url
        mixpanelTrack("Photo Video Copy Loaded", { project_id: this.projectIdValue, photo_id: photo.id })
      } else if (photo.playback_status === "failed") {
        this._showVideoNote("cannotPlay")
      } else {
        this._pollVideoCopy(photo)
      }
    }, 15000)
  }

  // ── Display ───────────────────────────────────────────────────────

  _showPhoto(index) {
    const photo = this._photos[index]
    if (!photo) return

    const isVideo = !!photo.is_video

    // Toggle image/video + editing controls based on media type
    if (this.hasRotateBtnTarget) this.rotateBtnTarget.classList.toggle("hidden", isVideo)
    if (this.hasMarkupBtnTarget) this.markupBtnTarget.classList.toggle("hidden", isVideo)

    // Apply stored + pending rotation via CSS (images only; videos never rotate)
    const url = photo.optimized_url || photo.original_url || photo.thumbnail_url
    const totalRotation = isVideo ? 0 : (photo.rotation || 0) + (photo._extraRotation || 0)
    if (this.hasImageWrapperTarget) {
      this.imageWrapperTarget.style.transition = "none"
      this.imageWrapperTarget.style.transform = totalRotation ? `rotate(${totalRotation}deg)` : ""
    }

    if (isVideo) {
      // Stop any previous playback, clear any image-era annotation state
      this.imageTarget.src = ""
      this.imageTarget.classList.add("hidden")
      if (this._annotationSC) { this._annotationSC.dispose(); this._annotationSC = null }
      if (this.hasAnnotationCanvasTarget) this.annotationCanvasTarget.style.display = "none"
      if (this.hasAnnotationTooltipTarget) this.annotationTooltipTarget.classList.add("hidden")

      if (this.hasVideoTarget) {
        const video = this.videoTarget
        try { video.pause() } catch (_) {}
        video.classList.remove("hidden")
        const loaded = url && video.src === url
        // The still the server took from it, until it plays.
        if (photo.thumbnail_url) video.poster = photo.thumbnail_url
        else video.removeAttribute("poster")
        this._watchVideo(photo)
        if (url && !loaded) video.src = url
        else this._videoCheck?.() // the same video again: it won't load twice
      }
    } else {
      this._stopVideoWatch()
      if (this.hasVideoTarget) {
        try { this.videoTarget.pause() } catch (_) {}
        this.videoTarget.removeAttribute("src")
        this.videoTarget.load()
        this.videoTarget.classList.add("hidden")
      }

      if (url) {
        this.imageTarget.style.opacity = "0"
        this.imageTarget.style.transition = "none"
        this.imageTarget.onload = null
        this.imageTarget.onerror = null
        this.imageTarget.addEventListener("load", () => {
          this.imageTarget.style.transition = "opacity 0.2s ease"
          this.imageTarget.style.opacity = "1"
        }, { once: true })
        if (photo.original_url) {
          this.imageTarget.addEventListener("error", () => {
            if (this.imageTarget.src !== photo.original_url) {
              // Drop crossorigin for fallback — old Procore photos without
              // optimized versions return 404 without CORS headers, which
              // poisons the browser's CORS state for subsequent requests.
              this.imageTarget.removeAttribute("crossorigin")
              this.imageTarget.src = photo.original_url
            }
          }, { once: true })
        }
        // tmp uploads lack a separate CORS cache entry — drop crossorigin to avoid conflict
        if (url.includes("/uploads/tmp/")) {
          this.imageTarget.removeAttribute("crossorigin")
        } else {
          this.imageTarget.setAttribute("crossorigin", "anonymous")
        }
        this.imageTarget.src = url
        this.imageTarget.classList.remove("hidden")
      } else {
        this.imageTarget.src = ""
        this.imageTarget.classList.add("hidden")
      }

      // Render annotation overlay (images only)
      this._renderAnnotationOverlay(photo)
    }

    // Counter
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${index + 1} / ${this._photos.length}`
    }

    // Source badge
    if (this.hasSourceBadgeTarget) {
      const sourceLabel = this._sourceLabel(photo.imageable_type)
      if (sourceLabel) {
        this.sourceBadgeTarget.classList.remove("hidden")
        const detail = photo.source_detail ? ` ${photo.source_detail}` : ""
        this.sourceLabelTarget.textContent = `From ${sourceLabel}${detail}`
        this._sourceUrl = this._buildSourceUrl(photo.imageable_type, photo.imageable_id)
        const hasLink = !!this._sourceUrl
        this.sourceDetailWrapTarget.classList.toggle("hidden", !hasLink)
        this.sourceBadgeTarget.classList.toggle("cursor-pointer", hasLink)
        this.sourceBadgeTarget.classList.toggle("hover:bg-gray-200", hasLink)
      } else {
        this.sourceBadgeTarget.classList.add("hidden")
      }
    }
    const lang = document.documentElement.lang || "en"
    const dateFormat = { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }
    if (this.hasTakenLabelTarget) {
      if (photo.taken_at) {
        // Zone-less ISO string — parsed as local, so the camera's
        // wall-clock time shows unshifted for every viewer
        const t = new Date(photo.taken_at)
        this.takenLabelTarget.textContent = `Taken ${t.toLocaleDateString(lang, dateFormat)}`
        this.takenLabelTarget.classList.remove("hidden")
      } else {
        this.takenLabelTarget.classList.add("hidden")
      }
    }
    if (this.hasDateLabelTarget) {
      const d = new Date(photo.created_at)
      const label = d.toLocaleDateString(lang, dateFormat)
      // Only prefix "Added" when a taken date is shown alongside
      this.dateLabelTarget.textContent = photo.taken_at ? `Added ${label}` : label
    }
    if (this.hasUserLabelTarget) {
      this.userLabelTarget.textContent = photo.user_name || ""
    }

    // What the AI sees (description + tags). Hidden by default — the AI summary
    // stays behind the scenes (still indexed for search); a toggle reveals it.
    if (this.hasAiToggleRowTarget) {
      const hasAi = !!photo.ai_description
      this.aiToggleRowTarget.classList.toggle("hidden", !hasAi)
      // Always start collapsed when a new photo opens.
      if (this.hasAiRowTarget) this.aiRowTarget.classList.add("hidden")
      if (this.hasAiChevronTarget) this.aiChevronTarget.style.transform = ""
      if (hasAi) {
        this.aiDescTarget.textContent = photo.ai_description
        this.aiTagsTarget.innerHTML = ""
        ;(photo.ai_tags || []).slice(0, 10).forEach(tag => {
          const chip = document.createElement("span")
          chip.className = "inline-flex items-center rounded-full bg-gray-100 text-gray-600 px-2 py-px"
          chip.style.fontSize = "12px"
          chip.textContent = tag
          this.aiTagsTarget.appendChild(chip)
        })
      }
    }
    if (this.hasRadarSectionTarget) {
      this.radarSectionTarget.classList.add("hidden")
      this.radarSectionTarget.classList.remove("flex")
      this.radarContentTarget.innerHTML = ""
    }
    this._clearRadarBoxes()
    if (this.hasRadarBtnTarget) this.radarBtnTarget.classList.toggle("hidden", isVideo)
    this._radarBusy = false

    // Capture location — row only shows when the photo carries GPS data
    if (this.hasLocationRowTarget) {
      const hasLocation = photo.latitude != null && photo.longitude != null
      this.locationRowTarget.classList.toggle("hidden", !hasLocation)
      if (this.hasLocationCoordsTarget) {
        // Trim trailing zeros: 42.586283, -114.451
        const fmt = v => String(parseFloat(Number(v).toFixed(6)))
        this.locationCoordsTarget.textContent = hasLocation ? `${fmt(photo.latitude)}, ${fmt(photo.longitude)}` : ""
      }
      this._collapseLocationMap(true)
    }

    // Compass bearing — which way the camera faced when the shot was taken
    if (this.hasFacingRowTarget) {
      const facing = this._formatHeading(photo.heading)
      this.facingRowTarget.classList.toggle("hidden", !facing)
      if (facing) {
        if (this.hasFacingLabelTarget) this.facingLabelTarget.textContent = facing
        // Arrow points the way the camera was aimed, with north up.
        if (this.hasFacingArrowTarget) {
          this.facingArrowTarget.style.transform = `rotate(${Number(photo.heading)}deg)`
        }
      }
    }

    // Only show delete button for own photos
    if (this.hasDeletePhotoBtnTarget) {
      this.deletePhotoBtnTarget.classList.toggle("hidden", (photo.owner_id || photo.user_id) !== this.currentUserIdValue)
    }

    // Cover photo: stills only, and not for read-only members
    if (this.hasCoverBtnTarget) {
      this.coverBtnTarget.classList.toggle("hidden", !this.canSetCoverValue || !!photo.is_video)
      this._toggleCoverSpinner(false)
    }

    // Load comments
    this._loadComments(photo.id)

    // Preload adjacent
    this._preloadAdjacent(index)
  }

  openSource() {
    if (this._sourceUrl) window.open(this._sourceUrl, "_blank")
  }

  // ── Capture location map ──────────────────────────────────────────

  // Reveal/hide the AI description (collapsed by default per photo).
  toggleAiRow() {
    if (!this.hasAiRowTarget) return
    const nowHidden = this.aiRowTarget.classList.toggle("hidden")
    if (this.hasAiChevronTarget) {
      this.aiChevronTarget.style.transform = nowHidden ? "" : "rotate(180deg)"
    }
  }

  toggleLocationMap() {
    const photo = this._photos[this._index]
    if (!photo || photo.latitude == null || photo.longitude == null) return

    if (this.mapWrapTarget.classList.contains("hidden")) {
      // Embedded Google Maps, satellite layer (t=k), zoomable/pannable
      const src = `https://maps.google.com/maps?q=${photo.latitude},${photo.longitude}&t=k&z=18&output=embed`
      if (this.mapFrameTarget.getAttribute("src") !== src) this.mapFrameTarget.src = src
      this.mapWrapTarget.classList.remove("hidden")
      this.locationChevronTarget.classList.add("rotate-180")
      mixpanelTrack("Photo Location Viewed", {
        project_id: this.projectIdValue,
        photo_id: photo.id,
        photo_source: photo.imageable_type
      })
    } else {
      this._collapseLocationMap()
    }
  }

  _collapseLocationMap(clearFrame = false) {
    if (!this.hasMapWrapTarget) return
    this.mapWrapTarget.classList.add("hidden")
    if (this.hasLocationChevronTarget) this.locationChevronTarget.classList.remove("rotate-180")
    // Drop the iframe src when switching photos so a stale map never shows
    if (clearFrame && this.hasMapFrameTarget) this.mapFrameTarget.removeAttribute("src")
  }

  // Bearing as the field says it: "NE · 47°". Empty for every photo with
  // no compass reading — anything shot before the app carried a compass,
  // and anything picked from the gallery.
  _formatHeading(heading) {
    const isNumeric = typeof heading === "number" || (typeof heading === "string" && heading.trim() !== "")
    const deg = isNumeric ? Number(heading) : NaN
    if (!isFinite(deg)) return ""
    const points = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"]
    const normalized = ((deg % 360) + 360) % 360
    // 359.6 rounds to 360, which reads wrong on a dial that stops at 359.
    return `${points[Math.round(normalized / 22.5) % 16]} · ${Math.round(normalized) % 360}°`
  }

  _buildSourceUrl(type, id) {
    if (!id) return null
    const base = `/projects/${this.projectIdValue}`
    if (type === "Punchlist") return `${base}/action_items/${id}`
    if (type === "DailyReport") return `${base}/daily_reports/${id}`
    return null
  }

  _sourceLabel(type) {
    const labels = {
      "DailyReport": "Report",
      "Punchlist": "Action Item",
      "ManualPhoto": "Manual Upload",
      "ProjectChecklistComment": "Checklist",
      "Project": "Project"
    }
    return labels[type] || type || "Photo"
  }

  _preloadAdjacent(index) {
    [index - 1, index + 1].forEach(i => {
      const p = this._photos[i]
      if (p) {
        if (p.is_video) return // videos are too large to preload
        const url = p.optimized_url || p.original_url || ""
        if (!url) return
        const img = new Image()
        img.crossOrigin = "anonymous"
        img.onerror = () => {
          // Optimized version missing — preload original without crossorigin
          if (p.original_url && img.src !== p.original_url) {
            img.crossOrigin = null
            img.src = p.original_url
          }
        }
        img.src = url
      }
    })
  }

  // ── Comments ──────────────────────────────────────────────────────

  async _loadComments(photoId) {
    if (!this.hasCommentListTarget) return
    this.commentListTarget.innerHTML = '<p class="text-xs text-gray-400 text-center">Loading...</p>'

    try {
      const res = await fetch(`${this.baseUrlValue}/${photoId}/comments.json`, {
        headers: { "Accept": "application/json" }
      })
      const comments = await res.json()
      this._renderComments(comments)
    } catch (e) {
      this.commentListTarget.innerHTML = ""
    }
  }

  _renderComments(comments) {
    // Keep the raw text by id: the edit modal reads it from here, never from
    // a data attribute (Larry 2026-09-06 — a comment with an inch mark, like
    // 24" curb, broke the attribute and the edit box came up cut off).
    this._commentsById = {}
    comments.forEach(c => { this._commentsById[String(c.id)] = c })
    this.commentListTarget.innerHTML = ""
    if (comments.length === 0) {
      this.commentListTarget.innerHTML = '<p class="text-xs text-gray-400 text-center py-4">No comments yet</p>'
      return
    }

    comments.forEach(c => {
      const el = document.createElement("div")
      el.className = "text-sm"
      el.dataset.commentId = c.id

      const d = new Date(c.created_at)
      const lang = document.documentElement.lang || "en"
      const timeStr = d.toLocaleDateString(lang, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })

      const escapedName = this._escapeHtml(c.user.fullname)
      const escapedText = this._escapeHtml(c.text)

      el.innerHTML = `
        <div class="flex items-center gap-2 mb-0.5">
          <span class="font-medium text-gray-800 text-xs">${escapedName}</span>
          <span class="text-xs text-gray-400">${timeStr}</span>
          ${c.its_me && !c.is_annotation ? `<div class="ml-auto flex gap-1">
            <button data-action="photo-lightbox#editComment" data-comment-id="${c.id}" data-comment-text="${escapedText}"
                    class="text-gray-400 hover:text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5"><path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" /></svg>
            </button>
            <button data-action="photo-lightbox#deleteComment" data-comment-id="${c.id}"
                    class="text-gray-400 hover:text-red-500">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" /></svg>
            </button>
          </div>` : ""}
        </div>
        <p class="${c.is_annotation ? 'text-gray-400 italic' : 'text-gray-600'}">${escapedText}</p>
      `
      this.commentListTarget.appendChild(el)
    })
  }

  async submitComment(e) {
    e.preventDefault()
    const text = this.commentInputTarget.value.trim()
    if (!text) return

    const photo = this._photos[this._index]
    if (!photo) return

    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const res = await fetch(`${this.baseUrlValue}/${photo.id}/comment`, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ text })
      })
      const comment = await res.json()

      this.commentInputTarget.value = ""
      this._loadComments(photo.id)

      // Update comment count in photo data + tile badge
      photo.comment_count = (photo.comment_count || 0) + 1
      this._updatePhotosControllerTile(photo.id, { comment_count: photo.comment_count })
    } catch (e) {
      console.error("Failed to submit comment:", e)
    }
  }

  editComment(e) {
    this._editingCommentId = e.currentTarget.dataset.commentId
    const stored = this._commentsById?.[String(this._editingCommentId)]
    this.editCommentInputTarget.value = stored ? stored.text : (e.currentTarget.dataset.commentText || "")
    this.editCommentModalTarget.classList.remove("hidden")
    this.editCommentInputTarget.focus()
  }

  cancelEditComment() {
    this.editCommentModalTarget.classList.add("hidden")
    this._editingCommentId = null
  }

  async confirmEditComment() {
    const newText = this.editCommentInputTarget.value.trim()
    if (!newText) return

    const photo = this._photos[this._index]
    if (!photo || !this._editingCommentId) return

    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      await fetch(`${this.baseUrlValue}/${photo.id}/comment`, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ text: newText, comment_id: this._editingCommentId })
      })
      this._loadComments(photo.id)
    } catch (e) {
      console.error("Failed to edit comment:", e)
    }

    this.editCommentModalTarget.classList.add("hidden")
    this._editingCommentId = null
  }

  deleteComment(e) {
    this._deletingCommentId = e.currentTarget.dataset.commentId
    this.deleteCommentModalTarget.classList.remove("hidden")
  }

  cancelDeleteComment() {
    this.deleteCommentModalTarget.classList.add("hidden")
    this._deletingCommentId = null
  }

  async confirmDeleteComment() {
    if (!this._deletingCommentId) return

    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      await fetch(`/projects/${this.projectIdValue}/comments/${this._deletingCommentId}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": token, "Accept": "application/json" }
      })

      const photo = this._photos[this._index]
      if (photo) {
        photo.comment_count = Math.max(0, (photo.comment_count || 1) - 1)
        this._loadComments(photo.id)
        this._updatePhotosControllerTile(photo.id, { comment_count: photo.comment_count })
      }
    } catch (e) {
      console.error("Failed to delete comment:", e)
    }

    this.deleteCommentModalTarget.classList.add("hidden")
    this._deletingCommentId = null
  }

  // ── Actions ───────────────────────────────────────────────────────

  // ── AI Radar — scan this photo against specs, drawings & safety ──

  runRadar() {
    const photo = this._photos[this._index]
    if (!photo || photo.is_video || this._radarBusy) return
    this._radarBusy = true

    this.radarIconTarget.classList.add("hidden")
    this.radarSpinnerTarget.classList.remove("hidden")
    this.radarSectionTarget.classList.remove("hidden")
    this.radarSectionTarget.classList.add("flex")
    this.radarContentTarget.innerHTML = ""
    const note = document.createElement("p")
    note.className = "text-gray-400 italic"
    note.style.fontSize = "13px"
    note.textContent = "Scanning against your specs, drawings and safety standards…"
    this.radarContentTarget.appendChild(note)

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(`${this.baseUrlValue}/${photo.id}/radar`, {
      method: "POST",
      headers: { "X-CSRF-Token": token || "", "Accept": "application/json" },
      credentials: "same-origin"
    })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => this._renderRadar(photo, data))
      .catch(() => {
        this.radarContentTarget.innerHTML = ""
        const err = document.createElement("p")
        err.className = "text-gray-500"
        err.style.fontSize = "13px"
        err.textContent = "The AI spot check isn't available right now — please try again in a moment."
        this.radarContentTarget.appendChild(err)
      })
      .finally(() => {
        this._radarBusy = false
        this.radarIconTarget.classList.remove("hidden")
        this.radarSpinnerTarget.classList.add("hidden")
      })
  }

  closeRadar() {
    this.radarSectionTarget.classList.add("hidden")
    this.radarSectionTarget.classList.remove("flex")
    this._clearRadarBoxes()
  }

  _renderRadar(photo, data) {
    const box = this.radarContentTarget
    box.innerHTML = ""
    this._clearRadarBoxes()
    this._radarCards = []

    if (data.summary) {
      const sum = document.createElement("p")
      sum.className = "text-gray-600 mb-1"
      sum.style.fontSize = "13px"
      sum.textContent = data.summary
      box.appendChild(sum)
    }

    const findings = Array.isArray(data.findings) ? data.findings : []
    if (!findings.length) {
      const none = document.createElement("p")
      none.className = "text-gray-500"
      none.style.fontSize = "13px"
      none.textContent = "Nothing stood out — no document or safety flags from what's visible."
      box.appendChild(none)
    }

    const LENS = { specs: "Specs", drawings: "Drawings", safety: "Safety" }
    findings.forEach((f, i) => {
      const check = f.verdict === "check"
      const card = document.createElement("div")
      card.className = `rounded-lg border px-2.5 py-2 ${check ? "border-amber-200 bg-amber-50" : "border-green-200 bg-green-50"}`

      const spots = Array.isArray(f.boxes) ? f.boxes : (f.box ? [f.box] : [])
      const head = document.createElement("div")
      head.className = `font-medium ${check ? "text-amber-800" : "text-green-800"}`
      head.style.fontSize = "13px"
      head.textContent = `${spots.length ? `${i + 1} · ` : ""}${check ? "⚠ " : "✓ "}${LENS[f.lens] || "Specs"} · ${f.title}`
      card.appendChild(head)

      // Click the card → light up its numbered spot(s) on the photo. Findings
      // without a specific spot flash the whole frame instead of doing nothing.
      card.classList.add("cursor-pointer")
      if (spots.length) {
        card.title = spots.length > 1 ? `Show the ${spots.length} places this applies in the photo` : "Show where this is in the photo"
        card.addEventListener("click", () => this._focusRadarBox(i))
      } else {
        card.title = "Applies to the whole scene"
        card.addEventListener("click", () => this._flashWholeScene())
      }

      if (f.note) {
        const note = document.createElement("p")
        note.className = "text-gray-700 mt-0.5"
        note.style.fontSize = "13px"
        note.textContent = f.note
        card.appendChild(note)
      }

      if (f.citation) {
        const cite = document.createElement("p")
        cite.className = "text-gray-500 mt-1"
        cite.style.fontSize = "12px"
        cite.textContent = `${f.citation.label} · p. ${f.citation.page}`
        card.appendChild(cite)
      }

      if (f.code && f.code.name) {
        const codeLine = document.createElement("p")
        codeLine.className = "mt-1"
        codeLine.style.fontSize = "12px"
        const a = document.createElement("a")
        a.href = f.code.url || "#"
        a.target = "_blank"
        a.rel = "noopener noreferrer"
        a.className = "text-amber-800 font-medium underline hover:text-amber-900"
        a.textContent = `${f.code.name} ↗`
        codeLine.appendChild(a)
        codeLine.appendChild(document.createTextNode(" — verify against the official text"))
        card.appendChild(codeLine)
      }

      if (check) {
        const btnWrap = document.createElement("div")
        btnWrap.className = "mt-1.5"
        const btn = document.createElement("button")
        btn.type = "button"
        btn.className = "inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-amber-200 text-amber-800 hover:bg-amber-100 font-medium cursor-pointer"
        btn.style.fontSize = "12px"
        btn.textContent = "→ Create action item"
        btn.addEventListener("click", (e) => { e.stopPropagation(); this._createRadarItem(photo, f, btn) })
        btnWrap.appendChild(btn)
        card.appendChild(btnWrap)
      }

      box.appendChild(card)
      this._radarCards.push(card)
    })

    this._renderRadarBoxes(findings)
  }

  // ── Radar spot markers — numbered boxes over the photo, linked to cards ──

  _clearRadarBoxes() {
    if (this.hasRadarBoxesTarget) this.radarBoxesTarget.innerHTML = ""
    clearTimeout(this._radarDimTimer)
  }

  _renderRadarBoxes(findings) {
    if (!this.hasRadarBoxesTarget) return
    this.radarBoxesTarget.innerHTML = ""

    findings.forEach((f, i) => {
      const spots = Array.isArray(f.boxes) ? f.boxes : (f.box ? [f.box] : [])
      const check = f.verdict === "check"

      spots.forEach((boxCoords) => {
        const [y1, x1, y2, x2] = boxCoords

        const el = document.createElement("button")
        el.type = "button"
        el.setAttribute("data-radar-box", String(i))
        el.title = f.title
        el.className = "absolute rounded-md pointer-events-auto cursor-pointer"
        el.style.top = `${y1 / 10}%`
        el.style.left = `${x1 / 10}%`
        el.style.height = `${(y2 - y1) / 10}%`
        el.style.width = `${(x2 - x1) / 10}%`
        el.style.border = `2px solid ${check ? "#f59e0b" : "#22c55e"}`
        el.style.boxShadow = "0 0 0 1px rgba(0,0,0,.45)"
        el.style.transition = "opacity .25s ease, border-width .15s ease"

        const tag = document.createElement("span")
        tag.className = "absolute flex items-center justify-center rounded-full text-white font-semibold"
        tag.style.cssText = `top:-9px;left:-9px;width:18px;height:18px;font-size:11px;background:${check ? "#f59e0b" : "#22c55e"};box-shadow:0 0 0 1px rgba(0,0,0,.35)`
        tag.textContent = String(i + 1)
        el.appendChild(tag)

        // Click any spot → highlight all of this finding's spots and bring
        // its card into view.
        el.addEventListener("click", (e) => {
          e.stopPropagation()
          this._focusRadarBox(i, true)
        })
        this.radarBoxesTarget.appendChild(el)
      })
    })
  }

  // Whole-scene findings: pulse a border around the entire photo so the
  // click always answers, even with no specific spot to point at.
  _flashWholeScene() {
    if (!this.hasRadarBoxesTarget) return
    const el = document.createElement("div")
    el.className = "absolute inset-0 rounded-md pointer-events-none"
    el.style.border = "3px solid rgba(147, 51, 234, .85)"
    el.style.boxShadow = "inset 0 0 0 1px rgba(0,0,0,.4)"
    el.style.transition = "opacity .5s ease"
    this.radarBoxesTarget.appendChild(el)
    setTimeout(() => { el.style.opacity = "0" }, 1100)
    setTimeout(() => el.remove(), 1700)
  }

  // Emphasize one spot (dim the rest briefly); optionally scroll its card
  // into view in the Radar column and flash it.
  _focusRadarBox(i, scrollCard = false) {
    if (this.hasRadarBoxesTarget) {
      this.radarBoxesTarget.querySelectorAll("[data-radar-box]").forEach(el => {
        const on = el.getAttribute("data-radar-box") === String(i)
        el.style.opacity = on ? "1" : "0.2"
        el.style.borderWidth = on ? "3px" : "2px"
      })
      clearTimeout(this._radarDimTimer)
      this._radarDimTimer = setTimeout(() => {
        if (!this.hasRadarBoxesTarget) return
        this.radarBoxesTarget.querySelectorAll("[data-radar-box]").forEach(el => {
          el.style.opacity = "1"
          el.style.borderWidth = "2px"
        })
      }, 2600)
    }

    const card = (this._radarCards || [])[i]
    if (card) {
      if (scrollCard) card.scrollIntoView({ behavior: "smooth", block: "center" })
      card.style.transition = "box-shadow .3s ease"
      card.style.boxShadow = "0 0 0 2px rgba(147, 51, 234, .65)"
      clearTimeout(card._flashTimer)
      card._flashTimer = setTimeout(() => { card.style.boxShadow = "" }, 1600)
    }
  }

  _createRadarItem(photo, finding, btn) {
    btn.disabled = true
    btn.style.opacity = "0.6"
    btn.textContent = "Creating…"

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(`${this.baseUrlValue}/${photo.id}/radar_action_item`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token || "", "Accept": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ title: finding.title, note: finding.note, verdict: finding.verdict })
    })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(() => {
        btn.textContent = "✓ Action item created"
        btn.className = "inline-flex items-center gap-1 px-2 py-1 rounded-md text-green-700 font-medium"
        btn.style.fontSize = "12px"
        mixpanelTrack("Action Item Created", { source: "photo_radar", project_id: this.projectIdValue })
      })
      .catch(() => {
        btn.disabled = false
        btn.style.opacity = ""
        btn.textContent = "→ Create action item"
      })
  }

  rotatePhoto() {
    const photo = this._photos[this._index]
    if (!photo || photo.is_video) return

    // Track extra rotation applied this session (on top of DB-stored value)
    photo._extraRotation = ((photo._extraRotation || 0) + 90) % 360
    const totalRotation = (photo.rotation || 0) + photo._extraRotation

    // Instant visual rotation via CSS (on wrapper so annotations rotate too)
    const target = this.hasImageWrapperTarget ? this.imageWrapperTarget : this.imageTarget
    target.style.transition = "transform 0.2s ease"
    target.style.transform = `rotate(${totalRotation}deg)`

    // Persist to DB (instant — just a column update)
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(`${this.baseUrlValue}/${photo.id}/rotate`, {
      method: "POST",
      headers: { "X-CSRF-Token": token, "Accept": "application/json" }
    }).then(res => res.json()).then(data => {
      if (data.success) {
        // Sync: move extra into stored rotation
        photo.rotation = data.rotation
        photo._extraRotation = 0
        // Update grid thumbnail rotation
        this._getPhotosController()?.updatePhotoTile(photo.id, { rotation: data.rotation })
      }
    }).catch(e => console.error("Failed to rotate:", e))
  }

  // Make this photo the project's cover image — the old app's "Make Project
  // Profile Picture" button, back where it used to live (UVU 2026-08-14).
  // Confirms first, and asks whether the pick should survive later uploads.
  setAsProjectPhoto() {
    const photo = this._photos[this._index]
    if (!photo || photo.is_video) return

    // Locked is the default every time the dialog opens.
    if (this.hasCoverLockCheckboxTarget) this.coverLockCheckboxTarget.checked = true
    this.coverConfirmModalTarget.classList.remove("hidden")
  }

  cancelSetAsProjectPhoto() {
    this.coverConfirmModalTarget.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  async confirmSetAsProjectPhoto() {
    this.coverConfirmModalTarget.classList.add("hidden")

    const photo = this._photos[this._index]
    if (!photo || photo.is_video) return

    const lock = this.hasCoverLockCheckboxTarget ? this.coverLockCheckboxTarget.checked : true

    this._toggleCoverSpinner(true)
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const res = await fetch(`${this.baseUrlValue}/${photo.id}/set_as_project_photo`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": token,
          "Accept": "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ lock: lock })
      })
      const data = await res.json().catch(() => ({}))

      if (!res.ok || !data.success) {
        this._showToast(data.error || "Couldn't set the project photo. Please try again.", "alert")
        return
      }

      this._showToast(data.locked
        ? "Project photo updated"
        : "Project photo updated — the next photo added will replace it")
      mixpanelTrack("Project Photo Set", {
        project_id: this.projectIdValue,
        photo_id: photo.id,
        photo_source: photo.imageable_type,
        locked: !!data.locked
      })
    } catch (e) {
      console.error("Failed to set project photo:", e)
      this._showToast("Couldn't set the project photo. Please try again.", "alert")
    } finally {
      this._toggleCoverSpinner(false)
    }
  }

  _toggleCoverSpinner(busy) {
    if (!this.hasCoverBtnTarget) return
    this.coverBtnTarget.disabled = busy
    if (this.hasCoverIconTarget) this.coverIconTarget.classList.toggle("hidden", busy)
    if (this.hasCoverSpinnerTarget) this.coverSpinnerTarget.classList.toggle("hidden", !busy)
  }

  // The lightbox overlay sits at the maximum z-index, so a toast prepended to
  // <body> would paint behind it. Appending puts it after the overlay in the
  // DOM, which wins the tie.
  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.appendChild(el)
  }

  deletePhoto() {
    this.deleteConfirmModalTarget.classList.remove("hidden")
  }

  cancelDeletePhoto() {
    this.deleteConfirmModalTarget.classList.add("hidden")
  }

  async confirmDeletePhoto() {
    this.deleteConfirmModalTarget.classList.add("hidden")

    const photo = this._photos[this._index]
    if (!photo) return

    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      await fetch(`${this.baseUrlValue}/${photo.id}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": token, "Accept": "application/json" }
      })

      // Remove from grid
      this._getPhotosController()?.removePhotoFromGrid(photo.id)

      // Remove from local array and navigate
      this._photos.splice(this._index, 1)
      if (this._photos.length === 0) {
        this.close()
      } else {
        this._index = Math.min(this._index, this._photos.length - 1)
        this._showPhoto(this._index)
      }
    } catch (e) {
      console.error("Failed to delete photo:", e)
    }
  }

  async downloadPhoto() {
    const photo = this._photos[this._index]
    if (!photo || !photo.original_url) return

    this.downloadIconTarget.style.display = "none"
    this.downloadSpinnerTarget.style.display = ""
    this.downloadSpinnerTarget.classList.remove("hidden")
    this.downloadBtnTarget.disabled = true

    try {
      const resp = await fetch(photo.original_url)
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = photo.filename || `photo_${photo.id}${photo.is_video ? ".mp4" : ".jpg"}`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error("Failed to download photo:", e)
    } finally {
      this.downloadIconTarget.style.display = ""
      this.downloadSpinnerTarget.style.display = "none"
      this.downloadBtnTarget.disabled = false
    }
  }

  openMarkup() {
    const photo = this._photos[this._index]
    if (!photo || photo.is_video) return

    const markupCtrl = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller~="photo-markup"]'),
      "photo-markup"
    )
    if (markupCtrl) {
      const imageUrl = photo.optimized_url || photo.original_url
      this.overlayTarget.style.display = "none"
      markupCtrl.open({
        photoId: photo.id,
        imageUrl,
        existingAnnotations: photo.annotations || null,
        currentUserName: this.currentUserNameValue,
        onClose: () => { this.overlayTarget.style.display = "" }
      })
    }
  }

  // ── Annotation overlay ───────────────────────────────────────────

  async _renderAnnotationOverlay(photo) {
    // Dispose any previous StaticCanvas instance
    if (this._annotationSC) {
      this._annotationSC.dispose()
      this._annotationSC = null
    }
    // Remove previous mousemove listener
    if (this._annotationMouseMove) {
      this.annotationCanvasTarget?.removeEventListener("mousemove", this._annotationMouseMove)
      this.annotationCanvasTarget?.removeEventListener("mouseleave", this._annotationMouseLeave)
      this._annotationMouseMove = null
      this._annotationMouseLeave = null
    }
    this._annotationObjects = null
    if (this.hasAnnotationTooltipTarget) {
      this.annotationTooltipTarget.classList.add("hidden")
    }

    if (!this.hasAnnotationCanvasTarget) return
    const canvasEl = this.annotationCanvasTarget

    if (!photo.annotations?.objects?.length) {
      canvasEl.style.display = "none"
      return
    }

    // Wait for the main image to load so the wrapper div has correct dimensions
    await new Promise(resolve => {
      if (this.imageTarget.complete && this.imageTarget.naturalWidth > 0) resolve()
      else this.imageTarget.addEventListener("load", resolve, { once: true })
    })

    // Check we're still showing the same photo (user may have navigated)
    const current = this._photos[this._index]
    if (!current || current.id !== photo.id) return

    const w = photo.annotations.width || 800
    const h = photo.annotations.height || 600

    // Set the canvas backing-store resolution to annotation dimensions.
    // CSS (w-full h-full) stretches it to match the photo display size.
    canvasEl.width = w
    canvasEl.height = h
    // Remove stale Fabric attribute from previous dispose (async cleanup)
    canvasEl.removeAttribute("data-fabric")
    canvasEl.classList.remove("lower-canvas")

    try {
      const { StaticCanvas } = await import("fabric")

      const sc = new StaticCanvas(canvasEl, {
        width: w,
        height: h,
        enableRetinaScaling: false
      })

      const rawObjects = photo.annotations.objects
      await sc.loadFromJSON({ objects: rawObjects })

      // Fabric.js loadFromJSON doesn't restore custom properties —
      // manually copy annotation metadata from raw JSON to Fabric objects
      const objects = sc.getObjects()
      for (let i = 0; i < objects.length && i < rawObjects.length; i++) {
        if (rawObjects[i]._annotatedBy) {
          objects[i]._annotatedBy = rawObjects[i]._annotatedBy
          objects[i]._annotatedAt = rawObjects[i]._annotatedAt
        }
      }

      sc.renderAll()

      canvasEl.style.display = ""
      this._annotationSC = sc

      // If any objects have author metadata, enable hover tooltips
      const hasMetadata = objects.some(o => o._annotatedBy)
      if (hasMetadata && this.hasAnnotationTooltipTarget) {
        this._annotationObjects = objects
        canvasEl.style.pointerEvents = ""

        this._annotationMouseMove = (e) => this._onAnnotationMouseMove(e, canvasEl, w, h)
        this._annotationMouseLeave = () => this.annotationTooltipTarget.classList.add("hidden")
        canvasEl.addEventListener("mousemove", this._annotationMouseMove)
        canvasEl.addEventListener("mouseleave", this._annotationMouseLeave)
      } else {
        canvasEl.style.pointerEvents = "none"
      }
    } catch (e) {
      console.warn("Failed to render annotation overlay:", e)
      canvasEl.style.display = "none"
    }
  }

  _onAnnotationMouseMove(event, canvasEl, canvasW, canvasH) {
    if (!this._annotationObjects) return

    // Convert mouse position from CSS-scaled space to canvas coordinate space
    const rect = canvasEl.getBoundingClientRect()
    const scaleX = canvasW / rect.width
    const scaleY = canvasH / rect.height
    const x = (event.clientX - rect.left) * scaleX
    const y = (event.clientY - rect.top) * scaleY

    // Find topmost object under the cursor (iterate in reverse for z-order)
    let hit = null
    for (let i = this._annotationObjects.length - 1; i >= 0; i--) {
      const obj = this._annotationObjects[i]
      if (!obj._annotatedBy) continue
      const br = obj.getBoundingRect()
      if (x >= br.left && x <= br.left + br.width && y >= br.top && y <= br.top + br.height) {
        hit = obj
        break
      }
    }

    const tooltip = this.annotationTooltipTarget
    if (hit) {
      this._showAnnotationTooltip(hit, event)
    } else {
      tooltip.classList.add("hidden")
    }
  }

  _showAnnotationTooltip(obj, event) {
    const tooltip = this.annotationTooltipTarget
    const name = obj._annotatedBy
    const date = obj._annotatedAt ? new Date(obj._annotatedAt) : null
    const lang = document.documentElement.lang || "en"

    let html = `<span class="font-medium">${this._escapeHtml(name)}</span>`
    if (date) {
      const dateStr = date.toLocaleDateString(lang, {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit"
      })
      html += `<span class="text-gray-300 ml-1.5">${dateStr}</span>`
    }
    tooltip.innerHTML = html
    tooltip.classList.remove("hidden")
    this._positionTooltip(event)
  }

  _positionTooltip(event) {
    const tooltip = this.annotationTooltipTarget
    const wrapper = this.imageWrapperTarget
    const rect = wrapper.getBoundingClientRect()

    // Position relative to the image wrapper
    let x = event.clientX - rect.left + 12
    let y = event.clientY - rect.top - 32

    // Keep tooltip within wrapper bounds
    const tw = tooltip.offsetWidth
    const th = tooltip.offsetHeight
    if (x + tw > rect.width) x = event.clientX - rect.left - tw - 8
    if (y < 0) y = event.clientY - rect.top + 16

    tooltip.style.left = `${x}px`
    tooltip.style.top = `${y}px`
  }

  _updatePhotosControllerTile(photoId, data) {
    const photosCtrl = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller~="photos"]'),
      "photos"
    )
    if (photosCtrl) photosCtrl.updatePhotoTile(photoId, data)
  }

  // ── Keyboard ──────────────────────────────────────────────────────

  _handleKeydown(e) {
    if (e.key === "Escape") { this.close(); return }
    if (e.key === "ArrowLeft") { this.prev(); return }
    if (e.key === "ArrowRight") { this.next(); return }
  }

  // ── Helpers ───────────────────────────────────────────────────────

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    // innerHTML leaves quotes alone; escape them too so the text is safe inside attributes.
    return div.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }

  _getPhotosController() {
    const el = document.querySelector('[data-controller~="photos"]')
    return el ? this.application.getControllerForElementAndIdentifier(el, "photos") : null
  }
}
